import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import type {
  GuidanceKind,
  MonitoringActivity,
  MonitoringAttestation,
  RuntimeControlStatus,
} from './monitoring-types';

import { getDesignTokens } from '~/client/components/base/core';

const GUIDANCE_LABELS: Record<GuidanceKind, string> = {
  inform: 'Inform',
  warn: 'Warn',
  escalate: 'Escalate',
  decline: 'Decline',
  require_human_review: 'Human review',
};

interface ActivityChipProps {
  value: MonitoringActivity;
}

export const ActivityChip: React.FC<ActivityChipProps> = ({ value }) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  if (value === 'unavailable') {
    return (
      <Chip
        label="Signal unavailable"
        size="small"
        sx={{
          backgroundColor: 'transparent',
          color: t.textMuted,
          border: `1px solid ${t.borderSubtle}`,
        }}
      />
    );
  }

  const isActive = value === 'active';
  return (
    <Chip
      label={isActive ? 'Active' : 'Idle'}
      size="small"
      sx={{
        backgroundColor: isActive ? t.status.successBg : t.status.neutralBg,
        color: isActive ? t.status.success : t.status.neutral,
        border: 'none',
        fontWeight: 500,
      }}
    />
  );
};

interface AttestationChipProps {
  value: MonitoringAttestation;
}

export const AttestationChip: React.FC<AttestationChipProps> = ({ value }) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  if (value === 'not_applicable') {
    return null;
  }

  if (value === 'pending') {
    // H1 (attestation chain verification) is not yet built, so runtime
    // attestation is genuinely "not yet verified" — a neutral signal, never
    // the 'unverified' tamper-gap alarm nor a silent gap. (Spec F5.)
    return (
      <Chip
        label="Verification pending"
        size="small"
        sx={{
          backgroundColor: t.status.neutralBg,
          color: t.status.neutral,
          border: 'none',
          fontWeight: 500,
        }}
      />
    );
  }

  const isGap = value === 'unverified';
  return (
    <Chip
      label={isGap ? 'Attestation gap' : 'Attested ✓'}
      size="small"
      sx={{
        backgroundColor: isGap ? t.status.warningBg : t.status.successBg,
        color: isGap ? t.status.warning : t.status.success,
        border: 'none',
        fontWeight: 500,
      }}
    />
  );
};

const RUNTIME_STATUS_LABELS: Record<RuntimeControlStatus, string> = {
  uncovered: 'Never consulted',
  nominal: 'Nominal',
  watch: 'Watch',
  elevated: 'Elevated',
};

interface RuntimeStatusChipProps {
  /** Optional — undefined for data that predates this field (see
   * `MonitoringControlRecord.status`'s doc comment); renders nothing rather
   * than fabricate a claim. */
  value: RuntimeControlStatus | undefined;
}

/**
 * G2 interpretive/threshold layer (PHASE-0-BUILD-SPEC.md §G2, Decision D1
 * ratified) — the attention-level readout that makes Monitoring
 * "Testing-like." Framed as attention levels, never pass/fail:
 *   - 'uncovered' mirrors ActivityChip's "unavailable" treatment (muted,
 *     outlined, no color claim) — it's a COVERAGE gap (assigned but never
 *     consulted), not a failure, and "grey" keeps it visually distinct from
 *     the colored attention states below.
 *   - 'nominal' is a neutral filled chip (no attention needed).
 *   - 'watch' is amber, 'elevated' is red — RAB-system colors already used
 *     for warning/danger everywhere else in this design system, not a new
 *     one-off palette choice.
 */
export const RuntimeStatusChip: React.FC<RuntimeStatusChipProps> = ({
  value,
}) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  if (value === undefined) {
    return null;
  }

  if (value === 'uncovered') {
    return (
      <Chip
        label={RUNTIME_STATUS_LABELS.uncovered}
        size="small"
        sx={{
          backgroundColor: 'transparent',
          color: t.textMuted,
          border: `1px solid ${t.borderSubtle}`,
        }}
      />
    );
  }

  if (value !== 'nominal' && value !== 'watch' && value !== 'elevated') {
    // Defensive fallback for any value outside the documented set (e.g.
    // future server value this client build doesn't know about yet) —
    // never fabricate a colored claim for a status we don't recognize.
    return null;
  }

  const toneByStatus: Record<
    'nominal' | 'watch' | 'elevated',
    { color: string; backgroundColor: string }
  > = {
    nominal: { color: t.status.neutral, backgroundColor: t.status.neutralBg },
    watch: { color: t.status.warning, backgroundColor: t.status.warningBg },
    elevated: { color: t.status.danger, backgroundColor: t.status.dangerBg },
  };
  const tone = toneByStatus[value];

  return (
    <Chip
      label={RUNTIME_STATUS_LABELS[value]}
      size="small"
      sx={{
        backgroundColor: tone.backgroundColor,
        color: tone.color,
        border: 'none',
        fontWeight: 500,
      }}
    />
  );
};

interface GuidanceChipProps {
  value: GuidanceKind;
}

export const GuidanceChip: React.FC<GuidanceChipProps> = ({ value }) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  return (
    <Chip
      label={GUIDANCE_LABELS[value] ?? value}
      size="small"
      sx={{
        backgroundColor: t.status.infoBg,
        color: t.status.info,
        border: 'none',
      }}
    />
  );
};
